import { ClassicCard } from '@braze/web-sdk';
import { AgreementCode, InvestorAccount } from '@services/appsync/types';
import braze from '@services/braze';
import intercom from '@services/intercom';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { UNACCEPTED_AGREEMENT, UNACCEPTED_TERMS, UNCONFIRMED_EMAIL } from './constants';
import { Alert } from './types';

interface InboxModelProps {
  alerts: Alert[];
  messages: ClassicCard[];
  newIntercomUnreadMessageCount: number;
}
interface InboxModelActions {
  checkForAlerts: (investor: InvestorAccount | null | undefined) => void;
  checkForMessages: () => ClassicCard[];
  checkForIntercomMessages: () => void;
  getMessageById: (id: string) => ClassicCard | undefined;
  markMessageAsViewed: (card: ClassicCard) => void;
  markMessageAsDismissed: (card: ClassicCard) => void;
  markMessageAsClicked: (card: ClassicCard) => void;
  listenForMessages: () => void;
}
export const InboxModel = createStore(
  persist<InboxModelProps & InboxModelActions>(
    (set, get) => ({
      alerts: [],
      checkForAlerts: (investor) => {
        if (!investor) return [];

        const alerts: Alert[] = [];
        // TODO: Implement later
        // const needsKYCreview = investor?.needs_manual_kyc_aml_review === true ?? false;
        // const hasubmittedKYC = investor?.manual_kyc_aml_review?.submitted_at ?? false;
        const hasConfirmedEmail = !!investor?.email_verified_at;
        const hasAcceptedTerms =
          !investor?.unaccepted_agreements ||
          !investor?.unaccepted_agreements.some((ua) =>
            [AgreementCode.DWOLLA_TOU_PRIVACY, AgreementCode.TOU_PRIVACY].includes(ua),
          );
        const hasAcceptedAgreement = !investor?.unaccepted_agreements?.includes(AgreementCode.ADVISORY);
        // TODO: Implement later
        // if (needsKYCreview && !hasubmittedKYC) alerts.push(UPLOAD_PHOTO_ID);
        // if (shouldUpdateSuitabilitySurvey(investor)) alerts.push(REVIEW_SUITABILITY);
        if (!hasConfirmedEmail) alerts.push(UNCONFIRMED_EMAIL);
        if (!hasAcceptedTerms) alerts.push(UNACCEPTED_TERMS);
        if (!hasAcceptedAgreement) alerts.push(UNACCEPTED_AGREEMENT);

        set({
          alerts,
        });
      },
      checkForIntercomMessages: () => {
        intercom.client('onUnreadCountChange', function (unreadCount: number) {
          set({ newIntercomUnreadMessageCount: unreadCount });
        });
      },
      checkForMessages: () => {
        braze.client.requestContentCardsRefresh();
        const contentCards = braze.client.getCachedContentCards();
        set({ messages: contentCards.cards as ClassicCard[] });
        return contentCards.cards as ClassicCard[];
      },
      getMessageById: (id) => {
        if (!id) return undefined;
        let messages = get().messages;
        if (!messages) messages = get().checkForMessages();
        const message = get().messages.find((m) => m.id === id);
        return message;
      },
      listenForMessages: () => {
        braze.client.subscribeToContentCardsUpdates(get().checkForMessages);
      },
      markMessageAsClicked: (card: ClassicCard) => {
        if (!card) return;
        braze.client.logContentCardClick(card);
      },
      markMessageAsDismissed: (card: ClassicCard) => {
        if (!card) return;
        braze.client.logCardDismissal(card);
      },
      markMessageAsViewed: (card: ClassicCard) => {
        if (!card) return;
        braze.client.logCardImpressions([card]);
      },
      messages: [],
      newIntercomUnreadMessageCount: 0,
    }),
    {
      getStorage: () => localStorage,
      name: 'inbox-model',
    },
  ),
);

export function useInboxModel(): InboxModelProps & InboxModelActions;
export function useInboxModel<T>(
  selector: (state: InboxModelProps & InboxModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useInboxModel<T>(
  selector?: (state: InboxModelProps & InboxModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line
  return useStore(InboxModel, selector!, equals);
}
